import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'umi';
import { Layout } from 'tea-component';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import SortBySelect from '@op/components/SelectComponents/SortBySelect';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import { GAME_CATEGORY } from '@opm/components/FilterCommon/constants/games';
import MarketContentBody from '@opm/components/MarketContentBody';
import ScrollBottomList from '@opm/components/ScrollBottomList';
import MediaPreviewModal, {
  useMediaPreviewModal,
} from '@opm/pages/CompetitorsAdCreatives/CompetitorsAdCreativesTable/MediaPreviewModal';
import useIntls from '@opm/hooks/useIntls';
import { TranslateConfigProvider } from '@opm/hooks/useTranslateConfig';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { CreativeTrendsPoint } from '@opm/operationLog/pages';
import { goToDetailsPage } from '@opm/utils/history';
import { DBO_TM } from '@op/utils/format';
import {
  useCreativeTrendsStats,
  useCreativeTrendsData,
  useTopicTypeColorMap,
} from './hooks';
import {
  ICreativeItem,
  ITopCreative,
  trendKey,
  ITrendItemBase,
} from './types';
import { SORT_OPTIONS } from './constants';
import StatusTabs from './components/StatusTabs';
import ViewSwitcher from './components/ViewSwitcher';
import CardView from './CardView';
import ListView from './ListView';
import TrendDrawer from './TrendDrawer';
import './index.scss';
import { adCreativesProcessor } from './processors';
import { useCTGameMap } from '@opm/utils/gameMap';
import { adCreativesStopTipsMap } from '@opm/components/BubbleCommon';
import marketConnect from '@opm/components/MarketConnect';
import { dealwithCreativeTrends } from '@opm/components/MarketConnect/utils/gamingAdIntelligence';
import { TByGameGenre } from '@opm/components/MarketConnect/utils/materialsList';
import { useGenreDateAutoTime } from '@opm/hooks/useGenreDateAutoTime';

const { Content } = Layout;

const userDefaultValue = {
  ...DBO_TM.filterRangeDay(13, 0),
  date_type: 'daily',
};

interface ICreativeTrendsStudioEmbedProps {
  hidePageHeader?: boolean;
  hideGameCategoryTabs?: boolean;
  enableSourcePageLog?: boolean;
  defaultFilter?: Record<string, unknown>;
  filterInstanceId?: string;
  /** 品类 → 最新数据日期映射（来自 API config.by_game_genre，日期格式 YYYY-MM-DD） */
  byGameGenre?: TByGameGenre;
}

/**
 * Creative Trends 列表页
 *
 * 布局：FilterCommon → FilterBoxCommon(StatusTabs + SortBy / Search + ViewSwitcher) → Card/List 双视图 + TrendDrawer
 */
function CreativeTrends({
  hidePageHeader = false,
  hideGameCategoryTabs = false,
  enableSourcePageLog = true,
  defaultFilter,
  filterInstanceId,
  byGameGenre,
}: ICreativeTrendsStudioEmbedProps = {}) {
  DBOM_POINT.useInitPage({
    buriedPoint: CreativeTrendsPoint,
    triggerPagePoint: enableSourcePageLog,
  });
  const { intl } = useIntls();
  // 稳定化 defaultFilter 引用，避免 marketConnect 每次渲染传新对象导致下游 useCallback 重建
  const stableDefaultFilter = useMemo(
    () => defaultFilter,
    [defaultFilter?.start_time, defaultFilter?.end_time, defaultFilter?.date_type],
  );

  const mergedUserDefaultValue = useMemo(
    () => ({
      ...userDefaultValue,
      ...stableDefaultFilter,
    }),
    [stableDefaultFilter],
  );

  

  // ─── 筛选器 ───
  const {
    filter,
    setFilter,
    loading: filterLoading,
    filterRef,
    ...filterProps
  } = useFilterCommon({
    keys: ['CTGame', 'CTMultiDateRange', 'CTCategory', 'ADChannel'],
    instanceId: filterInstanceId,
  });

  // 品类切换自动更新时间
  const { onGenreChange } = useGenreDateAutoTime({
    byGameGenre,
    dayRange: 13,
    filterRef,
    filter,
  });

  // ─── 游戏元信息字典（cond.materials_config_games）：id → meta，下发给 CardView/ListView/TrendDrawer ───
  const { gameMap } = useCTGameMap();

  // ─── Topic Type chip 配色映射（cond.creative_trends_config_topic_types）：L1.label_en → IAITagColor ───
  //    在主页面统一拉一次后通过 props 下发，避免每个 cell 重复请求
  const { topicTypeColorMap } = useTopicTypeColorMap();

  // ─── Drawer 状态 ───
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ITrendItemBase | null>(
    null,
  );

  // ─── Drawer 关闭时恢复左侧菜单展开 ───
  const dispatch = useDispatch();

  // ─── Drawer 筛选条件（透传给 RelatedCreatives） ───
  const drawerFilter = useMemo(() => {
    if (!drawerVisible || !selectedRecord?.topic) return undefined;
    return {
      ...filter,
      game_id: selectedRecord.game_id,
      topic: selectedRecord.topic,
      source_type: selectedRecord.source_type,
    };
  }, [drawerVisible, selectedRecord, filter]);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    openOrCloseMenuBlock(true);
  }, [dispatch]);

  // ─── 列表数据（双模式） ───
  const {
    cardRecords,
    tableRecords,
    total,
    listMax,
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    loading: dataLoading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    statusTab,
    setStatusTab,
    sortBy,
    setSortBy,
    keyword,
    setKeyword,
    viewMode,
    setViewMode,
  } = useCreativeTrendsData({
    filter,
    defaultViewMode: 'card',
    closeDrawer: handleDrawerClose,
  });

  // ─── StatusTab 数量（独立 stats 接口，需同步 search_text） ───
  const { counts } = useCreativeTrendsStats({ filter, searchText: keyword });

  const openOrCloseMenuBlock = (value: boolean) => {
    dispatch({
      type: 'opinionMarketingMenu/toggleMenuExpand',
      payload: { menuExpand: value },
    });
  };

  // ─── 媒体预览弹窗 ───
  const {
    visible: mediaVisible,
    open: mediaOpen,
    close: mediaClose,
    previewData,
  } = useMediaPreviewModal();

  // ─── 事件处理 ───
  const handleCardClick = useCallback((record: ITrendItemBase) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
    DBOM_POINT.btn('card_click', {
      game_id: record.game_id,
      topic: record.topic,
    });
    // 关闭 Drawer 时恢复左侧菜单展开
    openOrCloseMenuBlock(false);
  }, []);

  const handleRowClick = useCallback((record: ITrendItemBase) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
    DBOM_POINT.btn('row_click', {
      game_id: record.game_id,
      topic: record.topic,
    });
  }, []);

  const handleGoToDetail = useCallback(
    (record: ITrendItemBase, type = 'list') => {
      setDrawerVisible(false);
      DBOM_POINT.btn(
        type === 'drawer' ? 'drawer_view_all' : 'drawer_title_click',
        { game_id: record.game_id, topic: record.topic },
      );
      goToDetailsPage({
        filter: {
          ...filter,
          game_id: record.game_id,
          topic: record.topic,
          topic_en: record.topic_en,
          source_type: record.source_type,
        },
        navigateUrl: '/marketing/CreativeTrends/Details',
      });
    },
    [filter],
  );

  const handleTitleClick = useCallback(
    (record: ITrendItemBase) => {
      handleGoToDetail(record, 'list');
    },
    [handleGoToDetail],
  );

  // 缩略图点击：转换为 MediaPreviewModal 数据结构
  // useMediaPreviewModal.open() 内部仅读 type / app_name / preview_img / origin_url / resources，
  // 其它字段不传，最小化适配
  const handleThumbnailClick = useCallback(
    (item: ITopCreative | ICreativeItem) => {
      const videoUrl = item.video_url || '';
      const previewItem: any = {
        type: ['video'],
        app_name: (item as any).title || '',
        preview_img: item.preview_img || '',
        origin_url: item.origin_url || '',
        // copeResources 内部 JSON.parse 后取 [0] 作为视频/图片资源
        resources: JSON.stringify([videoUrl]),
      };
      mediaOpen(previewItem);
    },
    [mediaOpen],
  );

  // ─── 排序变更（带埋点） ───
  const handleSortChange = useCallback(
    (value: string) => {
      const option = SORT_OPTIONS.find((o) => o.value === value);
      const order = (option?.order || 'desc') as 'asc' | 'desc';
      setSortBy({ by: value, order });
      DBOM_POINT.btn('sort_change', { sort_item: value, sort_model: order });
    },
    [setSortBy],
  );

  // ─── StatusTab 切换（带埋点） ───
  const handleStatusTabChange = useCallback(
    (tab: any) => {
      setStatusTab(tab);
      DBOM_POINT.btn('status_tab_change', { tab });
    },
    [setStatusTab],
  );

  // ─── 视图切换（带埋点） ───
  const handleViewModeChange = useCallback(
    (mode: 'card' | 'table') => {
      setViewMode(mode);
      DBOM_POINT.btn('view_switch', { mode });
    },
    [setViewMode],
  );

  // ─── 搜索（带埋点） ───
  const handleSearch = useCallback(
    (value: string) => {
      setKeyword(value);
      if (value) {
        DBOM_POINT.btn('search', { keyword: value });
      }
    },
    [setKeyword],
  );

  return (
    <TranslateConfigProvider processor={adCreativesProcessor}>
      <ScrollBottomList>
        <Content className="dbom-ct-page">
          {!hidePageHeader && (
            <Content.Header title={intl('Creative Trends')} />
          )}
          <MarketContentBody
            loading={filterLoading}
            horseRaceLampTips={adCreativesStopTipsMap}
            filterSlot={
              <FilterCommon
                {...filterProps}
                userDefaultValue={mergedUserDefaultValue}
                filterConfigProps={{
                  CTGame: {
                    hideCategoryTabs: hideGameCategoryTabs,
                    defaultCategory: hideGameCategoryTabs
                      ? 'Arrows-like'
                      : GAME_CATEGORY.ALL,
                    onGenreChange,
                  },
                }}
                loadingHeight={150}
                useOriDefaultValue
                realTimeMode={true}
                // pagePath={PAGE_ENUM_MAP.creativeTrends}
                confirm={(value: any) => {
                  setFilter(value);
                }}
              />
            }
          >
            <div className="dbom-ct-row">
              <div className="dbom-ct-row-left">
                <FilterBoxCommon
                  left={[
                    {
                      key: 'segment',
                      // 必须非空：FilterBoxCommon 内部 map[key].defaultValue 在 key 不在内置 map 时会抛错
                      defaultValue: statusTab || 'hot',
                      element: (
                        <StatusTabs
                          value={statusTab}
                          onChange={handleStatusTabChange}
                          counts={counts}
                        />
                      ),
                    },
                    {
                      key: 'sortByElement',
                      defaultValue: sortBy?.by || 'impressions',
                      element: (
                        <SortBySelect
                          options={SORT_OPTIONS}
                          defaultSortBy={sortBy}
                          changeSortBy={(newSortBy: any) =>
                            handleSortChange(newSortBy?.by)
                          }
                          className="dbom-ct-sort-select"
                        />
                      ),
                    },
                  ]}
                  right={[
                    {
                      key: 'search',
                      defaultValue: keyword,
                      defaultParams: {
                        placeholder: intl('Search Trends'),
                        style: { width: 240 },
                        onSearch: (value: string) => handleSearch(value),
                        onClear: () => handleSearch(''),
                      },
                    },
                    {
                      key: 'viewSwitcher',
                      defaultValue: viewMode || 'card',
                      element: (
                        <ViewSwitcher
                          value={viewMode}
                          onChange={handleViewModeChange}
                        />
                      ),
                    },
                  ]}
                  onChange={() => {}}
                />
                <div
                  className={`dbom-ct-content ${
                    drawerVisible ? 'dbom-ct-content--drawer-open' : ''
                  }`}
                >
                  <div className="dbom-ct-content__main">
                    {viewMode === 'card' ? (
                      <CardView
                        data={cardRecords}
                        listMax={listMax}
                        gameMap={gameMap}
                        topicTypeColorMap={topicTypeColorMap}
                        isFirstLoading={isFirstLoading}
                        isEmpty={isEmpty}
                        isPaginationLoading={isPaginationLoading}
                        nextPage={loadNextPage}
                        onCardClick={handleCardClick}
                        onTitleClick={handleTitleClick}
                        onThumbnailClick={handleThumbnailClick}
                        selectedTrendKey={
                          drawerVisible ? trendKey(selectedRecord) : undefined
                        }
                        filter={filter}
                      />
                    ) : (
                      <ListView
                        data={tableRecords}
                        total={total}
                        loading={!!dataLoading}
                        listMax={listMax}
                        gameMap={gameMap}
                        topicTypeColorMap={topicTypeColorMap}
                        pageInfo={pageInfo}
                        changePageInfo={changeTablePageInfo}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        filter={filter}
                        onRowClick={handleRowClick}
                        onTitleClick={handleTitleClick}
                        onThumbnailClick={handleThumbnailClick}
                        selectedTrendKey={
                          drawerVisible ? trendKey(selectedRecord) : undefined
                        }
                      />
                    )}
                  </div>
                  {drawerVisible && (
                    <TrendDrawer
                      record={selectedRecord}
                      filter={drawerFilter}
                      gameMap={gameMap}
                      topicTypeColorMap={topicTypeColorMap}
                      onClose={handleDrawerClose}
                      onViewAll={(record) => handleGoToDetail(record, 'drawer')}
                      onTitleClick={(record) =>
                        handleGoToDetail(record, 'drawer')
                      }
                      onThumbnailClick={(item) => handleThumbnailClick(item)}
                    />
                  )}
                </div>
              </div>
            </div>

            <MediaPreviewModal
              visible={mediaVisible}
              onClose={mediaClose}
              {...(previewData || {})}
            />
          </MarketContentBody>
        </Content>
      </ScrollBottomList>
    </TranslateConfigProvider>
  );
}


const CREATIVE_TRENDS_LIST_API = "creative_trends/list";
export default marketConnect<ICreativeTrendsStudioEmbedProps>({
  ids: [CREATIVE_TRENDS_LIST_API],
  dealwith: dealwithCreativeTrends(CREATIVE_TRENDS_LIST_API),
})(CreativeTrends);
